// Performance monitoring utility for development
// Use this to track the improvements in navigation speed

class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.enabled = process.env.NODE_ENV === 'development';
  }

  // Track page navigation times
  startNavigation(routeName) {
    if (!this.enabled) return;
    
    this.metrics.set(`nav_${routeName}`, {
      start: performance.now(),
      route: routeName,
      type: 'navigation'
    });
  }

  endNavigation(routeName) {
    if (!this.enabled) return;
    
    const key = `nav_${routeName}`;
    const metric = this.metrics.get(key);
    
    if (metric) {
      const duration = performance.now() - metric.start;
      console.log(`🚀 Navigation to ${routeName}: ${duration.toFixed(2)}ms`);
      
      // Track if it's an improvement
      const previousBest = localStorage.getItem(`perf_${routeName}`);
      if (!previousBest || duration < parseFloat(previousBest)) {
        localStorage.setItem(`perf_${routeName}`, duration.toString());
        console.log(`✨ New performance record for ${routeName}!`);
      }
      
      this.metrics.delete(key);
    }
  }

  // Track cache hit rates
  trackCacheHit(cacheType, isHit) {
    if (!this.enabled) return;
    
    const key = `cache_${cacheType}`;
    const existing = this.metrics.get(key) || { hits: 0, misses: 0 };
    
    if (isHit) {
      existing.hits++;
    } else {
      existing.misses++;
    }
    
    this.metrics.set(key, existing);
    
    const total = existing.hits + existing.misses;
    const hitRate = (existing.hits / total * 100).toFixed(1);
    
    if (total % 10 === 0) { // Log every 10th request
      console.log(`📊 ${cacheType} cache hit rate: ${hitRate}% (${existing.hits}/${total})`);
    }
  }

  // Monitor Firestore query counts
  trackFirestoreQuery(collection, queryType = 'read') {
    if (!this.enabled) return;
    
    const key = `firestore_${collection}_${queryType}`;
    const count = this.metrics.get(key) || 0;
    this.metrics.set(key, count + 1);
    
    console.log(`🔥 Firestore ${queryType} on ${collection} (total: ${count + 1})`);
  }

  // Get performance summary
  getSummary() {
    if (!this.enabled) return null;
    
    const summary = {
      cacheStats: {},
      firestoreStats: {},
      navigationBests: {}
    };

    // Cache statistics
    for (const [key, value] of this.metrics.entries()) {
      if (key.startsWith('cache_')) {
        const cacheType = key.replace('cache_', '');
        const total = value.hits + value.misses;
        summary.cacheStats[cacheType] = {
          ...value,
          hitRate: total > 0 ? (value.hits / total * 100).toFixed(1) : 0
        };
      }
    }

    // Firestore statistics
    for (const [key, value] of this.metrics.entries()) {
      if (key.startsWith('firestore_')) {
        const parts = key.replace('firestore_', '').split('_');
        const collection = parts[0];
        const queryType = parts[1];
        
        if (!summary.firestoreStats[collection]) {
          summary.firestoreStats[collection] = {};
        }
        summary.firestoreStats[collection][queryType] = value;
      }
    }

    // Navigation bests from localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('perf_')) {
        const route = key.replace('perf_', '');
        const time = parseFloat(localStorage.getItem(key));
        summary.navigationBests[route] = `${time.toFixed(2)}ms`;
      }
    }

    return summary;
  }

  // Display performance dashboard in console
  showDashboard() {
    if (!this.enabled) return;
    
    const summary = this.getSummary();
    
    console.group('📊 Performance Dashboard');
    
    if (Object.keys(summary.cacheStats).length > 0) {
      console.group('💾 Cache Performance');
      Object.entries(summary.cacheStats).forEach(([type, stats]) => {
        console.log(`${type}: ${stats.hitRate}% hit rate (${stats.hits}/${stats.hits + stats.misses})`);
      });
      console.groupEnd();
    }
    
    if (Object.keys(summary.firestoreStats).length > 0) {
      console.group('🔥 Firestore Usage');
      Object.entries(summary.firestoreStats).forEach(([collection, stats]) => {
        console.log(`${collection}:`, stats);
      });
      console.groupEnd();
    }
    
    if (Object.keys(summary.navigationBests).length > 0) {
      console.group('🚀 Navigation Best Times');
      Object.entries(summary.navigationBests).forEach(([route, time]) => {
        console.log(`${route}: ${time}`);
      });
      console.groupEnd();
    }
    
    console.groupEnd();
  }

  // Clear all metrics
  reset() {
    this.metrics.clear();
    
    // Optionally clear localStorage performance records
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith('perf_')) {
        localStorage.removeItem(key);
      }
    }
    
    console.log('🧹 Performance metrics reset');
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor();

// Helper to wrap navigation functions
export const withNavigationTracking = (navigationFunc, routeName) => {
  return (...args) => {
    performanceMonitor.startNavigation(routeName);
    const result = navigationFunc(...args);
    
    // Track completion after a short delay to account for component mounting
    setTimeout(() => {
      performanceMonitor.endNavigation(routeName);
    }, 100);
    
    return result;
  };
};

// Add to window for easy access in dev tools
if (process.env.NODE_ENV === 'development') {
  window.performanceMonitor = performanceMonitor;
  window.showPerfDashboard = () => performanceMonitor.showDashboard();
  
  // Auto-show dashboard every 30 seconds
  setInterval(() => {
    performanceMonitor.showDashboard();
  }, 30000);
}

export default performanceMonitor; 